import type { Movie, MovieType } from "@/types/movie";
import { movies as rawMovies } from "@/mocks/movies";

export const movies = rawMovies as unknown as Movie[];

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export function getMoviesByType(type: MovieType): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids
    .map((id) => getMovieById(id))
    .filter((m): m is Movie => Boolean(m));
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}亿`;
  return `${views.toFixed(1)}万`;
}

export function formatPlayCount(views: number): string {
  return `${formatViews(views)}次播放`;
}

interface FilterParams {
  type?: string;
  region?: string;
  year?: string;
  genre?: string;
  keyword?: string;
}

export function filterMovies({
  type,
  region,
  year,
  genre,
  keyword,
}: FilterParams): Movie[] {
  let result = [...movies];

  if (type && type !== "全部") {
    result = result.filter((m) => m.type === type);
  }
  if (region && region !== "全部") {
    result = result.filter((m) => m.region === region);
  }
  if (year && year !== "全部") {
    result = result.filter((m) => String(m.year) === year);
  }
  if (genre && genre !== "全部") {
    result = result.filter(
      (m) => m.genres.includes(genre) || m.tags.includes(genre),
    );
  }
  if (keyword && keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    result = result.filter(
      (m) =>
        m.title.toLowerCase().includes(kw) ||
        (m.originalTitle || "").toLowerCase().includes(kw) ||
        m.genres.some((g) => g.toLowerCase().includes(kw)) ||
        m.tags.some((t) => t.toLowerCase().includes(kw)) ||
        m.actors.some((a) => a.toLowerCase().includes(kw)) ||
        m.director.toLowerCase().includes(kw),
    );
  }

  return result;
}

export function sortMovies(
  list: Movie[],
  sort: string,
): Movie[] {
  const result = [...list];
  if (sort === "hot") {
    result.sort((a, b) => b.views - a.views);
  } else if (sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort((a, b) => b.year - a.year || b.views - a.views);
  }
  return result;
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  return movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      if (m.type === movie.type) score += 3;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
      score += m.tags.filter((t) => movie.tags.includes(t)).length * 2;
      if (m.region === movie.region) score += 1;
      return { movie: m, score };
    })
    .sort((a, b) => b.score - a.score || b.movie.views - a.movie.views)
    .slice(0, limit)
    .map((item) => item.movie);
}

export function getGenresByType(type: string): string[] {
  const pool =
    type && type !== "全部"
      ? movies.filter((m) => m.type === type)
      : movies;
  const set = new Set<string>();
  pool.forEach((m) => m.genres.forEach((g) => set.add(g)));
  return ["全部", ...Array.from(set)];
}